/**
 * Terms of Service page - Brutalist minimalism
 */

import React from 'react';

export const Terms: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-24">
      {/* Header */}
      <div className="mb-10 md:mb-16">
        <h1 className="text-3xl md:text-5xl font-bold mb-3 md:mb-4">Terms of Service</h1>
        <div className="text-text-secondary font-mono text-xs md:text-sm">
          <span className="block">PermitLoop — Spot Labs Inc.</span>
          <span className="block">Effective Date: March 3, 2026</span>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-8 md:space-y-12 prose-brutalist">
        <div className="text-text-secondary leading-relaxed text-sm md:text-base">
          Please read these Terms of Service ("Terms") carefully before accessing or using PermitLoop (the "Service"), operated by Spot Labs Inc. ("Company," "we," "us," or "our"). By creating an account or otherwise accessing the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.
        </div>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">1. Description of Service</h2>
          <p className="text-text-secondary leading-relaxed">
            PermitLoop is an AI-powered software-as-a-service (SaaS) platform accessible at permitloop.ai that generates permitting matrix reports ("Matrices") for energy and data center development projects located in the United States. The Service aggregates publicly available regulatory information and applies artificial intelligence to produce structured summaries of permitting requirements at the federal, state, and local levels.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">2. Acceptance of Terms</h2>
          <p className="text-text-secondary leading-relaxed mb-4">
            By accessing or using the Service, you confirm that:
          </p>
          <ul className="space-y-2 text-text-secondary">
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>You are at least 18 years of age and have the legal capacity to enter into a binding agreement</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>You are using the Service on behalf of yourself or an organization you are authorized to represent</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="h-px w-3 bg-white mt-3 flex-shrink-0"></div>
              <span>You have read, understood, and agree to be bound by these Terms and our Privacy Policy, incorporated herein by reference</span>
            </li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">3. Account Registration</h2>
          <p className="text-text-secondary leading-relaxed mb-4">
            Access to the Service at any tier—including the free tier—requires account registration. You must create and maintain an active account to use PermitLoop in any capacity.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">4. Free Tier and Subscription Plans</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-bold mb-2 font-mono uppercase text-sm">4.1 Free Tier</h3>
              <p className="text-text-secondary leading-relaxed">
                New users must register for an account to access the Service. Upon completing registration, each account is entitled to generate one (1) complimentary permitting Matrix. Once the free Matrix has been generated, continued access to the Service requires a paid subscription.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2 font-mono uppercase text-sm">4.2 Paid Subscriptions</h3>
              <p className="text-text-secondary leading-relaxed">
                PermitLoop offers monthly and annual subscription options. Subscription pricing is displayed on the Service's pricing page and may be updated from time to time.
              </p>
            </div>
          </div>
        </section>

        <section className="card border-accent-yellow/20 bg-accent-yellow/5">
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">5. AI-Generated Content — Disclaimer</h2>
          <div className="space-y-4">
            <div className="text-accent-yellow font-mono text-sm uppercase mb-4">Important Notice</div>
            <p className="text-text-secondary leading-relaxed">
              The permitting Matrices generated by PermitLoop are produced by artificial intelligence systems and are provided for informational and preliminary research purposes only. They do not constitute legal advice, regulatory guidance, engineering recommendations, or investment advice.
            </p>
            <p className="text-text-secondary leading-relaxed font-bold">
              PRIOR TO MAKING ANY BUSINESS, INVESTMENT, CAPITAL DEPLOYMENT, FINANCING, CONSTRUCTION, OR REGULATORY FILING DECISIONS, YOU MUST INDEPENDENTLY VERIFY ALL INFORMATION CONTAINED IN A MATRIX WITH QUALIFIED LEGAL COUNSEL, LICENSED ENGINEERS, ENVIRONMENTAL CONSULTANTS, AND/OR APPLICABLE REGULATORY AUTHORITIES.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">6. Intellectual Property</h2>
          <p className="text-text-secondary leading-relaxed">
            The Service, including its software, user interface, design, branding, trademarks, and underlying technology, is owned by Spot Labs Inc. Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for your internal business purposes.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">7. Limitation of Liability</h2>
          <p className="text-text-secondary leading-relaxed">
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS. IN NO EVENT SHALL SPOT LABS INC.'S TOTAL CUMULATIVE LIABILITY EXCEED THE TOTAL FEES PAID BY YOU IN THE TWELVE (12) MONTHS IMMEDIATELY PRECEDING THE EVENT GIVING RISE TO THE CLAIM.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">8. Termination</h2>
          <p className="text-text-secondary leading-relaxed">
            You may cancel your subscription at any time through your account settings. We may suspend or terminate your account if you breach these Terms. Upon termination, your right to access the Service ceases immediately.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">9. Governing Law</h2>
          <p className="text-text-secondary leading-relaxed mb-4">
            These Terms are governed by the laws of the State of Delaware. Any dispute shall be resolved by binding arbitration administered by the American Arbitration Association.
          </p>
          <p className="text-text-secondary leading-relaxed font-bold">
            CLASS ACTION WAIVER: YOU AND SPOT LABS INC. EACH WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 uppercase tracking-tight">10. Contact Information</h2>
          <div className="space-y-2 text-text-secondary font-mono text-sm">
            <div className="flex gap-4">
              <span className="text-text-muted min-w-[120px]">Legal inquiries:</span>
              <a href="mailto:legal@permitloop.ai" className="text-white hover:underline">legal@permitloop.ai</a>
            </div>
            <div className="flex gap-4">
              <span className="text-text-muted min-w-[120px]">Support:</span>
              <a href="mailto:support@permitloop.ai" className="text-white hover:underline">support@permitloop.ai</a>
            </div>
            <div className="flex gap-4">
              <span className="text-text-muted min-w-[120px]">Website:</span>
              <a href="https://permitloop.ai" className="text-white hover:underline">permitloop.ai</a>
            </div>
            <div className="flex gap-4">
              <span className="text-text-muted min-w-[120px]">Address:</span>
              <span>Spot Labs Inc., 50 Prince St, New York, NY 10012</span>
            </div>
          </div>
        </section>

        <div className="text-text-muted font-mono text-xs pt-8 border-t border-border-dim">
          © 2026 Spot Labs Inc. All rights reserved. Last updated: March 3, 2026
        </div>
      </div>
    </div>
  );
};
